import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

import { operatorApiFetch } from '@services/operator/apiService';

import { QCFlowEvents, QCFlowStates } from './qcFlowMachine';

const QcParcelContext = createContext();

export const QcParcelProvider = ({ children, qcFlowState, triggerQcFlowEvent }) => {
  // Parcels 狀態
  const [parcels, setParcels] = useState([]);
  const [currentParcelIndex, setCurrentParcelIndex] = useState(0);

  // 用 ref 來存儲函數，避免循環依賴
  const checkAndAutoPackIfCompleteRef = useRef(null);
  const performParcelRef = useRef(null);

  // Video splitter injected by Index.jsx. Called after performParcel API success
  // to stop the current MediaRecorder session, enqueue a finalize task for the
  // just-closed session, and start a new session for the next parcel.
  //
  // Signature: (parcelId: string, parcelIndex: number) => Promise<void> | void
  //
  // Stored as ref (not state) because:
  //  1) We don't want re-renders when it's registered (it's a stable callback)
  //  2) performParcel reads it lazily so identity changes don't matter
  const videoSplitterRef = useRef(null);
  const setVideoSplitter = useCallback((fn) => {
    videoSplitterRef.current = fn;
  }, []);

  // 防止重複呼叫 pack API 的 guard
  const isPackingRef = useRef(false);

  // 新增一個 parcel
  const addNewParcel = useCallback(() => {
    setParcels(prev => [...prev, { parcel_items: [] }]);
    setCurrentParcelIndex(prev => prev + 1);
  }, []);

  // 切換到指定的 parcel
  const switchToParcel = useCallback((index) => {
    if (index >= 0 && index < parcels.length) {
      setCurrentParcelIndex(index);
    }
  }, [parcels.length]);

  // 執行裝箱動作（不包含影片上傳，影片由 Queue 管理）
  // currentQcFulfillment: 當前出貨單（必須傳入）
  // parcelsData 參數：傳入最新的 parcels 數據，避免使用過時的 state
  const performParcel = useCallback(async (currentQcFulfillment, parcelIndex, parcelsData = null) => {

    if (!currentQcFulfillment || !currentQcFulfillment.id) {
      console.error('[QcParcelContext][performParcel] ✗ No current fulfillment for parcel');
      return { success: false, error: 'no_current_fulfillment' };
    }

    const parcelsToUse = parcelsData || parcels;
    const currentParcel = parcelsToUse[parcelIndex];


    if (!currentParcel) {
      console.error('[QcParcelContext][performParcel] ✗ No parcel data at index:', parcelIndex);
      return { success: false, error: 'no_parcel_data' };
    }

    // 已經 pack 過的 parcel（有 parcel_id）不應再次 pack
    if (currentParcel.parcel_id) {
      console.warn('[QcParcelContext][performParcel] ⚠ Parcel already packed (parcel_id exists), skipping');
      return { success: true, data: { parcel_id: currentParcel.parcel_id } };
    }

    // 防止重複呼叫 pack API（多個 useEffect / render cycle 可能同時觸發）
    if (isPackingRef.current) {
      console.warn('[QcParcelContext][performParcel] ⚠ Pack already in progress, skipping duplicate call');
      return { success: false, error: 'pack_in_progress' };
    }
    isPackingRef.current = true;

    try {

      // 呼叫後端 API（只建立 parcel，不上傳影片）
      const response = await operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/pack`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: currentParcel.parcel_items,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.message || result.error || 'parcel_failed';
        console.error('[QcParcelContext][performParcel] ✗ API response not ok:', errorMessage);
        // 不清空 parcels state — 讓操作員看完錯誤後能補/改本地的 parcel_items 再重試。
        return { success: false, error: errorMessage, interpolations: result.interpolations || {} };
      }


      // 成功後，更新 parcels 的 parcel_id
      setParcels(prevParcels => {
        const newParcels = [...prevParcels];
        if (newParcels[parcelIndex]) {
          newParcels[parcelIndex] = {
            ...newParcels[parcelIndex],
            parcel_id: result.data?.parcel_id,
          };
        }
        return newParcels;
      });

      // Cut the video session and enqueue finalize for the just-packed parcel.
      // Runs AFTER backend success so a failed /pack never leaves an orphan session.
      // Splitter is registered by Index.jsx; null when no recorder is mounted (tests / no-camera).
      //
      // ⚠️ SKIP when this pack completes the fulfillment (last parcel)。
      // 心智模型:N 箱 = N 支影片。若這裡仍切,後續 60s 封箱倒數
      // (finishCompletedRecording) 會獨立成第 N+1 支,內容只是「pack 完之後
      // 的後續錄影」,沒有業務意義。改成讓 session 繼續錄,finishCompletedRecording
      // 接走那段 session 作為 last parcel 的影片(videoIndex = lastPackedIndex + 1)。
      const items = currentQcFulfillment.items || [];
      const allItemsShipped = items.length > 0 && items.every(item =>
        (item.shipped_quantity || 0) >= (item.quantity || 0)
      );
      const splitter = videoSplitterRef.current;
      if (splitter && result.data?.parcel_id && !allItemsShipped) {
        try {
          await splitter(result.data.parcel_id, parcelIndex);
        } catch (splitErr) {
          // Splitter failure should not flip the pack result — the parcel IS packed
          // on the backend. Log and continue; finalize can be retried later (separate plan #5).
          console.error('[QcParcelContext][performParcel] videoSplitter threw:', splitErr);
        }
      }

      return { success: true, data: result.data };
    } catch (err) {
      console.error('[QcParcelContext][performParcel] ✗✗✗ Exception occurred:', err);
      const fallbackMessage = err?.message || 'internal_server_error';
      return { success: false, error: fallbackMessage };
    } finally {
      isPackingRef.current = false;
    }
  }, [parcels]);

  // 更新 performParcelRef
  useEffect(() => {
    performParcelRef.current = performParcel;
  }, [performParcel]);

  // 檢查並自動 pack（如果所有項目都完成）
  // updatedParcels 參數：傳入最新的 parcels 數據，避免使用過時的 state
  // 回傳形狀:
  //   { skipped: true }         — 條件不符,沒嘗試 pack (state 不對 / 沒掃完 / 已 packed 等)
  //   { success: true }         — pack 成功,已 trigger ALL_ITEMS_COMPLETED
  //   { success: false, error, interpolations } — pack 失敗 (e.g. backend 422 duplicate_serial)
  //
  // ⚠️ 失敗時 caller 必須 surface 給 UI (setError + message),否則 user 會卡在
  //    SCANNING_ITEMS 卻完全不知道發生什麼事 — 這就是線上觀察到的 stuck bug 的成因。
  const checkAndAutoPackIfComplete = useCallback(async (updatedFulfillment, updatedParcels = null) => {
    // ⚠️ 狀態檢查：只有在 SCANNING_ITEMS 狀態才執行自動 pack
    if (qcFlowState !== QCFlowStates.SCANNING_ITEMS) {
      return { skipped: true };
    }

    if (!updatedFulfillment || !updatedFulfillment.items || updatedFulfillment.items.length === 0) {
      return { skipped: true };
    }

    // 檢查所有項目是否都完成
    const allItemsCompleted = updatedFulfillment.items.every(item => {
      const shipped = item.shipped_quantity || 0;
      const required = item.quantity || 0;
      return shipped >= required;
    });

    if (!allItemsCompleted) {
      return { skipped: true };
    }

    // 使用傳入的 updatedParcels 或 state 中的 parcels
    const parcelsToCheck = updatedParcels || parcels;
    const currentParcel = parcelsToCheck[currentParcelIndex];

    if (!currentParcel || !currentParcel.parcel_items || currentParcel.parcel_items.length === 0) {
      return { skipped: true };
    }

    // 已經 pack 過的 parcel 不需要再次 pack
    if (currentParcel.parcel_id) {
      return { skipped: true };
    }

    // 自動執行 pack（使用最新的 parcels 數據）
    try {
      const parcelResult = await performParcel(updatedFulfillment, currentParcelIndex, parcelsToCheck);

      if (parcelResult.success) {

        // 觸發 ALL_ITEMS_COMPLETED 事件，進入 AWAITING_RESCAN 狀態
        triggerQcFlowEvent(QCFlowEvents.ALL_ITEMS_COMPLETED, {
          orderId: updatedFulfillment.id,
          orderNumber: updatedFulfillment.name,
        });

        return { success: true };
      }
      console.error('[QcParcelContext] ✗ Auto-pack failed:', parcelResult.error);
      return {
        success: false,
        error: parcelResult.error,
        interpolations: parcelResult.interpolations || null,
      };

    } catch (err) {
      console.error('[QcParcelContext] ✗ Auto-pack exception:', err);
      return { success: false, error: err?.message || 'internal_server_error' };
    }
  }, [qcFlowState, parcels, currentParcelIndex, performParcel, triggerQcFlowEvent]);

  // 更新 ref（保留以防其他地方需要使用）
  useEffect(() => {
    checkAndAutoPackIfCompleteRef.current = checkAndAutoPackIfComplete;
  }, [checkAndAutoPackIfComplete]);

  // 更新指定 parcel 的商品列表
  const updateParcelItems = useCallback(async (currentQcFulfillment, parcelIndex, updatedItems) => {

    if (parcelIndex < 0 || parcelIndex >= parcels.length) {
      console.error('[QcParcelContext] Invalid parcel index:', parcelIndex);
      return { success: false, error: 'invalid_parcel_index' };
    }

    if (!currentQcFulfillment || !currentQcFulfillment.id) {
      console.error('[QcParcelContext] No current fulfillment');
      return { success: false, error: 'no_current_fulfillment' };
    }

    // 計算這次更新前後的數量變化
    const oldParcel = parcels[parcelIndex];
    const oldItems = oldParcel.parcel_items || [];


    // 先更新 parcels（在 state 更新之前計算新的 parcels）
    const updatedParcels = [...parcels];

    // 檢查更新後的 items 是否為空
    const isEmpty = !updatedItems || updatedItems.length === 0;

    if (isEmpty) {
      // 如果清空了，移除這個 parcel
      updatedParcels.splice(parcelIndex, 1);


      // 如果移除後沒有任何箱子了，建立一個新的空箱子
      if (updatedParcels.length === 0) {
        updatedParcels.push({ parcel_items: [] });
      }
    } else {
      // 否則只更新該 parcel 的 items
      updatedParcels[parcelIndex] = {
        ...updatedParcels[parcelIndex],
        parcel_items: updatedItems
      };
    }


    // 呼叫後端 API 儲存更新
    try {
      const parcelId = oldParcel.parcel_id;

      if (isEmpty && parcelId) {
        // 如果 parcel 清空且有 parcel_id，呼叫刪除 API
        const response = await operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/parcels/${parcelId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const result = await response.json();
          const errorMessage = result.message || result.error || 'delete_parcel_failed';
          console.error('[QcParcelContext] Delete parcel failed:', errorMessage);
          return { success: false, error: errorMessage };
        }

      } else if (!isEmpty && parcelId) {
        // 如果 parcel 有內容且有 parcel_id，呼叫更新 API
        const response = await operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/parcels/${parcelId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: updatedItems,
          }),
        });

        if (!response.ok) {
          const result = await response.json();
          const errorMessage = result.message || result.error || 'update_parcel_failed';
          console.error('[QcParcelContext] Update parcel failed:', errorMessage);
          return { success: false, error: errorMessage };
        }

      } else if (!isEmpty && !parcelId) {
        // 如果 parcel 有內容但沒有 parcel_id，呼叫建立 API
        const response = await operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/pack`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: updatedItems,
          }),
        });

        if (!response.ok) {
          const result = await response.json();
          const errorMessage = result.message || result.error || 'create_parcel_failed';
          console.error('[QcParcelContext] Create parcel failed:', errorMessage);
          return { success: false, error: errorMessage };
        }

        const result = await response.json();

        // 更新 parcel_id
        if (result.data?.parcel_id) {
          updatedParcels[parcelIndex] = {
            ...updatedParcels[parcelIndex],
            parcel_id: result.data.parcel_id,
          };
        }
      }
    } catch (err) {
      console.error('[QcParcelContext] updateParcelItems API error:', err);
      const fallbackMessage = err?.message || 'internal_server_error';
      return { success: false, error: fallbackMessage };
    }

    // 調整 currentParcelIndex（如果需要的話）
    let newCurrentParcelIndex = currentParcelIndex;

    if (isEmpty && updatedParcels.length > 0) {
      // 計算新的 currentParcelIndex
      if (parcelIndex === currentParcelIndex) {
        // 如果刪除的是當前箱子，切換到前一個（如果有的話）
        newCurrentParcelIndex = Math.max(0, Math.min(parcelIndex, updatedParcels.length - 1));
      } else if (parcelIndex < currentParcelIndex) {
        // 如果刪除的箱子在當前箱子之前，需要調整索引
        newCurrentParcelIndex = currentParcelIndex - 1;
      }
    }

    // 更新 parcels state
    setParcels(updatedParcels);

    // 如果移除了箱子，更新 currentParcelIndex
    if (isEmpty) {
      setCurrentParcelIndex(newCurrentParcelIndex);
    }


    return {
      success: true,
      parcelRemoved: isEmpty,
      newCurrentParcelIndex,
      updatedParcels  // 返回更新後的 parcels，供外部使用
    };
  }, [parcels, currentParcelIndex]);

  // 重置包裝狀態
  const resetParcels = useCallback(() => {
    setParcels([]);
    setCurrentParcelIndex(0);
  }, []);

  // 設置包裝狀態（用於載入訂單時初始化）
  const setParcelsState = useCallback((newParcels, newIndex = 0) => {
    setParcels(newParcels);
    setCurrentParcelIndex(newIndex);
  }, []);

  // 清除所有 parcels（包含呼叫後端 API 刪除）
  const clearAllParcels = useCallback(async (currentQcFulfillment) => {

    if (!currentQcFulfillment || !currentQcFulfillment.id) {
      console.error('[QcParcelContext][clearAllParcels] ✗ No current fulfillment');
      return { success: false, error: 'no_current_fulfillment' };
    }

    const parcelsToDelete = parcels.filter(p => p.parcel_id);

    if (parcelsToDelete.length === 0) {
      // 沒有需要刪除的後端 parcel，直接清空前端並初始化空箱子
      setParcels([{ parcel_items: [] }]);
      setCurrentParcelIndex(0);
      return { success: true, deletedCount: 0 };
    }

    try {
      // 依序刪除每個 parcel，記錄成功刪除的 parcel_id
      const deletedParcelIds = new Set();
      let failedCount = 0;

      for (const parcel of parcelsToDelete) {
        try {
          const response = await operatorApiFetch(`/fulfillments/${currentQcFulfillment.id}/parcels/${parcel.parcel_id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            const result = await response.json();
            const errorMessage = result.message || result.error || 'delete_parcel_failed';
            console.error(`[QcParcelContext][clearAllParcels] ✗ Failed to delete parcel ${parcel.parcel_id}:`, errorMessage);
            failedCount++;
          } else {
            deletedParcelIds.add(parcel.parcel_id);
          }
        } catch (err) {
          console.error(`[QcParcelContext][clearAllParcels] ✗ Exception deleting parcel ${parcel.parcel_id}:`, err);
          failedCount++;
        }
      }

      // 只移除後端成功刪除的 parcel 和沒有 parcel_id 的前端 parcel
      const remainingParcels = parcels.filter(p => p.parcel_id && !deletedParcelIds.has(p.parcel_id));

      // 確保至少有一個空箱子供繼續掃描
      if (remainingParcels.length === 0) {
        remainingParcels.push({ parcel_items: [] });
      }

      setParcels(remainingParcels);
      setCurrentParcelIndex(Math.min(0, remainingParcels.length - 1));

      return {
        success: failedCount === 0,
        deletedCount: deletedParcelIds.size,
        failedCount,
        remainingParcels
      };
    } catch (err) {
      console.error('[QcParcelContext][clearAllParcels] ✗✗✗ Exception occurred:', err);
      return { success: false, error: err?.message || 'internal_server_error' };
    }
  }, [parcels]);

  const value = {
    parcels,
    currentParcelIndex,
    addNewParcel,
    switchToParcel,
    performParcel,
    updateParcelItems,
    checkAndAutoPackIfComplete,
    resetParcels,
    setParcelsState,
    clearAllParcels,
    setVideoSplitter,
    // Export refs for external use
    performParcelRef,
    checkAndAutoPackIfCompleteRef,
    videoSplitterRef,
  };

  return (
    <QcParcelContext.Provider value={value}>
      {children}
    </QcParcelContext.Provider>
  );
};

export const useQcParcel = () => {
  const context = useContext(QcParcelContext);
  if (!context) {
    throw new Error('useQcParcel must be used within a QcParcelProvider');
  }
  return context;
};

export default QcParcelContext;
